/**
 *    @tableOfContent
 *      1. Dependencies
 *      2. Class
 *      3. Export class
 *
 */

/**
 *     @section 1. Dependencies
 */
import Helper from '../app/app--helper'
import MarkerIcon from './modules/marker-icon'

/**
 *     @section 2. Class
 */
class MapDetailPage {
    constructor() {
        if (document.querySelector('[data-map-open]')) {
            document.addEventListener('map:loaded', () => {
                this.initMap()
            })
        }
    }

    initMap() {
        const themePath = document.querySelector('[data-theme-path]')?.dataset.themePath
        let markersById = {}

        //
        // button behavior
        //
        const fieldSwitchEl = document.querySelector('.region-with-map__header .field--switch .field__control') as HTMLInputElement


        if (window.location.hash) {
            this.addHashToUrl(window.location.hash.replace('#', ''))
        }

        document.querySelectorAll('[data-map-open]').forEach((buttonEl) => {
            buttonEl.addEventListener('click', () => {
                const id = buttonEl.dataset.mapId
                const marker = markersById[id]

                // open popup
                google.maps.event.trigger(marker, 'click')

                if (!fieldSwitchEl.checked) {
                    // enable map
                    fieldSwitchEl.checked = true
                    // Create a new 'change' event
                    let event = new Event('change')
                    fieldSwitchEl.dispatchEvent(event)
                }

                // Add partnerId as hash to the URL
                this.addHashToUrl(buttonEl.dataset.partnerId)
            })

            buttonEl.addEventListener('mouseenter', () => {
                const id = buttonEl.dataset.mapId
                const marker = markersById[id]

                if (!buttonEl.classList.contains('visible')) {
                    google.maps.event.trigger(marker, 'mouseover')
                }
            })

            buttonEl.addEventListener('mouseleave', () => {
                const id = buttonEl.dataset.mapId
                const marker = markersById[id]

                if (!buttonEl.classList.contains('visible')) {
                    google.maps.event.trigger(marker, 'mouseout')
                }
            })

            // observe
            new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const id = entry.target.dataset.mapId
                        const marker = markersById[id]

                        if (entry.isIntersecting) {
                            entry.target.classList.add('visible')

                            // Scale the marker when hovered
                            marker.setIcon(MarkerIcon.get('#000000', 'medium'))

                            // highlight target
                            entry.target?.closest('.partner-details')?.classList.add('active')
                            setTimeout(() => {
                                entry.target?.closest('.partner-details')?.classList.remove('active')
                            }, Helper.transitionTime * 2)
                        } else {
                            entry.target.classList.remove('visible')
                            google.maps.event.trigger(marker, 'mouseout')

                            // remove hash from URL
                            // this.removeHashToUrl()
                        }
                    })
                },
                {
                    // root: document,
                    rootMargin: '0px 0px 0px 0px',
                    threshold: 0,
                }
            ).observe(buttonEl)
        })


        //
        // load map
        //
        let map: google.maps.Map
        async function initMap(): Promise<void> {
            //@ts-ignore
            const { Map } = await google.maps.importLibrary('maps')
            map = new Map(document.querySelector('.map--detailpage .map__vendor') as HTMLElement, {
                center: { lat: 52.52437, lng: 13.41053 },
                zoom: 4,// 8,
                maxZoom: 12,
                minZoom: 4,
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false
            })


            //
            // load polygon
            //
            const region = document.querySelector('[data-region]')?.dataset.region
            map.data.loadGeoJson(`${themePath}/dist/map/${region}.geojson`)
            map.data.loadGeoJson(`${themePath}/dist/map/complete-region.geojson`)

            map.data.setStyle( ( feature ) => {
                const name = feature.getProperty('name')
                let fillColor = '#fff'
                let fillOpacity = 0.3
                let strokeColor = '#8C8C8C'
                let strokeWeight = 2
                let strokeOpacity = 1

                if (name === 'outline') {
                    fillOpacity = 0
                    strokeColor = '#3871C1'
                    strokeWeight = 4
                    strokeOpacity = 0.5
                }

                return {
                    fillColor: fillColor,
                    fillOpacity: fillOpacity,
                    strokeColor: strokeColor,
                    strokeWeight: strokeWeight,
                    strokeOpacity: strokeOpacity
                }
            })

            //
            // add markers to map
            //

            document.querySelectorAll('[data-map-open]').forEach((buttonEl) => {
                const lat = parseFloat(buttonEl.dataset.mapLat)
                const lng = parseFloat(buttonEl.dataset.mapLng)
                const id = buttonEl.dataset.mapId
                const type = buttonEl.dataset.mapType

                const marker = new google.maps.Marker({
                    position: { lat: lat, lng: lng },
                    map: map,
                    icon: MarkerIcon.get(Helper.color[type]),
                    id: id,
                    type: type
                })

                markersById[id] = marker

                // open popup
                marker.addListener('click', () => {
                    // reset other markers
                    Object.entries(markersById).forEach(([key, value]) => {
                        if (value.id !== marker.id) {
                            // console.log(value)
                            setTimeout(() => {
                                google.maps.event.trigger(value, 'mouseout')
                            }, 100)
                        }
                    })

                    // Scale the marker when hovered
                    marker.setIcon(MarkerIcon.get(Helper.color[type], 'large'))

                    // scroll to target
                    const targetEl = document.querySelector(`[data-map-id="${marker.id}"]`)?.closest('.partner-details')
                    const targetElOffset = window.scrollY + (targetEl?.getBoundingClientRect().top ?? 0)
                    const pageOffset = 160
                    document.documentElement.scrollTo({
                        top: targetElOffset - pageOffset,
                        left: 0,
                        behavior: 'smooth',
                    })

                    // highlight target
                    targetEl?.classList.add('active')
                    setTimeout(() => {
                        targetEl?.classList.remove('active')
                    }, Helper.transitionTime * 2)

                    //
                    const breakpoint = Helper.getCurrentBreakpoint()
                    if (breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md') {
                        fieldSwitchEl.checked = false

                        // Create a new 'change' event
                        let event = new Event('change')
                        fieldSwitchEl.dispatchEvent(event)
                    }
                })

                // Add event listener for mouseover
                marker.addListener('mouseover', () => {
                    // Scale the marker when hovered
                    marker.setIcon(MarkerIcon.get(Helper.color[type], 'large'))
                })

                marker.addListener('mouseout', () => {
                    const buttonEl = document.querySelector(`[data-map-id="${marker.id}"]`)

                    if (!buttonEl?.classList.contains('visible')) {
                        // Scale the marker when hovered
                        marker.setIcon(MarkerIcon.get(Helper.color[type]))
                    }
                })
            })

            // zoom in
            let bounds = new google.maps.LatLngBounds()
            Object.entries(markersById).forEach(([key, value]) => {
                bounds.extend(value.position)
            })
            map.fitBounds(bounds)
        }

        initMap()
    }

    addHashToUrl(partnerId: string) {
        // Add partnerId as hash to the URL
        if (partnerId) {
            window.location.hash = `#${partnerId}`

            // add hash to all language links
            document.querySelectorAll('.links--language .language-link').forEach((link) => {
                let href = link.getAttribute('href') || ''
                if (href.includes('#')) {
                    href = href.split('#')[0]
                }

                // add new hash
                link.setAttribute('href', `${href}#${partnerId}`)
            })
        }
    }

    // removeHashToUrl() {
    //     // remove hash from URL
    //     if (window.location.hash) {
    //         history.replaceState(null, '', ' ');
    //     }

    //     // remove hash from all language links
    //     document.querySelectorAll('.links--language .language-link').forEach((link) => {
    //         let href = link.getAttribute('href') || ''

    //         // remove old hash
    //         if (href.includes('#')) {
    //             link.setAttribute('href', href.split('#')[0])
    //         }
    //     })
    // }
}

/**
 *     @section 3. Export class
 */

export default new MapDetailPage()

// end of map-detailpage.ts file
